/**
 * /admin/ambassadors — Founder dashboard for the ambassador/affiliate
 * program. Three jobs:
 *   1. Invite a new ambassador (email + optional handle + commission %)
 *   2. See every ambassador's clicks / signups / paying users / earnings
 *   3. Mark a payout paid (after the founder Venmos / PayPals offline)
 *
 * Admin-only — same auth pattern as the other /admin/* pages.
 * Wrapped in `AdminShell` for sidebar nav + dark-mode parity with
 * /admin/engagement, /admin/revenue, /admin/members, etc.
 * Not localised — admin surfaces are English-only per the i18n
 * leak-guard exempt list.
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Copy,
  Users,
  TrendingUp,
  Send,
  CheckCircle2,
  Loader2,
  RefreshCw,
  DollarSign,
  Plus,
  X,
  AlertTriangle,
  Inbox,
  ExternalLink,
} from "lucide-react";
import AdminShell from "../components/AdminShell";
import {
  adminApproveAmbassadorApplication,
  adminCreateAmbassadorInvite,
  adminListAmbassadorApplications,
  adminListAmbassadorInvites,
  adminListAmbassadors,
  adminMarkAmbassadorPayoutPaid,
  adminRejectAmbassadorApplication,
} from "../lib/api";

const fmtPct = (rate) => `${Math.round((rate || 0) * 100)}%`;
const fmtUsd = (n) =>
  (n ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};

// ─── shared admin-shell building blocks ──────────────────────────
// Match the visual language of AdminEngagement.jsx so the founder
// sees one coherent admin surface. Dark slate panels, white-on-dark
// inputs, beige accents for actions.
const DarkInput = (props) => (
  <input
    {...props}
    className={`w-full px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.03] text-white placeholder:text-white/30 focus:border-[#D4B483] focus:bg-white/[0.06] outline-none transition-colors ${props.className || ""}`}
  />
);

const DarkLabel = ({ children, required }) => (
  <label className="text-[10px] uppercase tracking-[0.22em] text-white/55 block mb-1.5">
    {children}
    {required && <span className="text-[#D4B483] ml-1">*</span>}
  </label>
);

const Panel = ({ children, title, meta, testid }) => (
  <section
    className="bg-white/[0.03] border border-white/8 rounded-2xl p-5 sm:p-6 mb-6"
    data-testid={testid}
  >
    {title && (
      <header className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <h2 className="text-base font-medium text-white">{title}</h2>
        {meta && (
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
            {meta}
          </span>
        )}
      </header>
    )}
    {children}
  </section>
);

// ─── invite form ─────────────────────────────────────────────────
const InviteForm = ({ onSent }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [rate, setRate] = useState("30");
  const [notes, setNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    setLastInvite(null);
    try {
      const data = await adminCreateAmbassadorInvite({
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        handle: handle.trim() || null,
        commission_rate: rate ? Number(rate) / 100 : null,
        notes: notes.trim() || null,
        send_email: sendEmail,
      });
      setLastInvite(data);
      toast.success(
        sendEmail
          ? `Invite sent to ${data.email}`
          : `Invite minted — copy the link below`,
      );
      setEmail("");
      setName("");
      setHandle("");
      setNotes("");
      onSent?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create invite.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    if (!lastInvite?.claim_url) return;
    navigator.clipboard?.writeText(lastInvite.claim_url);
    toast.success("Claim link copied to clipboard");
  };

  return (
    <Panel
      title="Invite an ambassador"
      meta="Resend → claim link"
      testid="admin-ambassador-invite-section"
    >
      <p className="text-sm text-white/55 mb-5 leading-relaxed">
        They get an email with a 1-click claim link. Once they pick a handle +
        payout method, they're live with their referral URL + coupon code.
      </p>
      <form
        onSubmit={submit}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <div className="sm:col-span-2">
          <DarkLabel required>Ambassador email</DarkLabel>
          <DarkInput
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="alex@example.com"
            data-testid="admin-ambassador-invite-email"
          />
        </div>
        <div>
          <DarkLabel>Name (optional)</DarkLabel>
          <DarkInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Alex"
            data-testid="admin-ambassador-invite-name"
          />
        </div>
        <div>
          <DarkLabel>Handle suggestion (optional)</DarkLabel>
          <DarkInput
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="alex"
            data-testid="admin-ambassador-invite-handle"
          />
        </div>
        <div>
          <DarkLabel>Commission %</DarkLabel>
          <DarkInput
            type="number"
            min="0"
            max="100"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            data-testid="admin-ambassador-invite-rate"
          />
        </div>
        <div className="flex items-end">
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-white/80">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="accent-[#D4B483]"
              data-testid="admin-ambassador-invite-send-email"
            />
            <span>Email them now</span>
          </label>
        </div>
        <div className="sm:col-span-2">
          <DarkLabel>Internal notes (admin only)</DarkLabel>
          <DarkInput
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="TikTok @glowup_alex · 60k followers · met at QueerTech"
            data-testid="admin-ambassador-invite-notes"
          />
        </div>
        <div className="sm:col-span-2 flex items-center gap-3 flex-wrap">
          <button
            type="submit"
            disabled={busy || !email}
            className="inline-flex items-center gap-2 bg-[#D4B483] text-[#1A1720] hover:bg-[#E5C99A] disabled:opacity-50 disabled:cursor-not-allowed px-5 py-2.5 rounded-full transition-colors text-sm font-medium"
            data-testid="admin-ambassador-invite-submit"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {sendEmail ? "Send invite" : "Generate link"}
          </button>
          {lastInvite && (
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/20 px-3 py-2 rounded-full transition-colors"
              data-testid="admin-ambassador-copy-claim-url"
            >
              <Copy className="h-3.5 w-3.5" />
              Copy claim link
            </button>
          )}
        </div>
      </form>
      {lastInvite && (
        <div className="mt-4 p-3 rounded-lg bg-white/[0.04] border border-white/8 text-xs text-white/70 break-all">
          <span className="text-[#D4B483] font-medium">Claim URL:</span>{" "}
          {lastInvite.claim_url}
        </div>
      )}
    </Panel>
  );
};

// ─── payout modal ────────────────────────────────────────────────
const PayoutModal = ({ ambassador, onClose, onPaid }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const pendingCents = Math.round((ambassador?.earnings_pending_usd || 0) * 100);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const amountCents = amount ? Math.round(Number(amount) * 100) : null;
      await adminMarkAmbassadorPayoutPaid({
        handle: ambassador.handle,
        amount_cents: amountCents,
        note: note || null,
      });
      toast.success(
        `Marked $${amount || (pendingCents / 100).toFixed(2)} paid to ${ambassador.handle}`,
      );
      onPaid?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payout marking failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!ambassador) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="admin-ambassador-payout-modal"
    >
      <div
        className="bg-[#141820] border border-white/10 rounded-2xl p-6 max-w-md w-full text-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-medium">
            Pay {ambassador.display_name || ambassador.handle}
          </h3>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-white/65 mb-4">
          Pending:{" "}
          <strong className="text-white">
            {fmtUsd(ambassador.earnings_pending_usd)}
          </strong>
          {ambassador.payout_method && (
            <>
              <br />
              Payout method:{" "}
              <code className="bg-white/[0.05] px-1 rounded text-[#D4B483]">
                {ambassador.payout_method}
              </code>
            </>
          )}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <DarkLabel>Amount (leave blank to pay all pending)</DarkLabel>
            <DarkInput
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Leave blank for ${fmtUsd(ambassador.earnings_pending_usd)}`}
              data-testid="admin-ambassador-payout-amount"
            />
          </div>
          <div>
            <DarkLabel>
              Note (e.g. &quot;Venmo 5/31&quot;, &quot;PayPal Sept batch&quot;)
            </DarkLabel>
            <DarkInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="admin-ambassador-payout-note"
            />
          </div>
          <button
            type="submit"
            disabled={busy || pendingCents <= 0}
            className="inline-flex items-center gap-2 bg-[#D4B483] text-[#1A1720] hover:bg-[#E5C99A] disabled:opacity-50 px-5 py-2.5 rounded-full transition-colors text-sm font-medium"
            data-testid="admin-ambassador-payout-submit"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Mark paid
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── applications queue ────────────────────────────────────────────
const ApplicationsPanel = ({ onApproved }) => {
  const [apps, setApps] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await adminListAmbassadorApplications({ limit: 200 });
      setApps(data.applications || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "load failed");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (app) => {
    if (busyId) return;
    setBusyId(app.application_id);
    try {
      const res = await adminApproveAmbassadorApplication(app.application_id, {});
      if (res?.sent_email) {
        toast.success(`Approved ${app.email} — invite email sent.`);
      } else {
        toast.success(
          `Approved ${app.email} — Resend skipped, claim link copied for you to send manually.`,
        );
      }
      if (res?.claim_url) {
        navigator.clipboard?.writeText(res.claim_url);
      }
      onApproved?.();
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't approve application.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (app) => {
    if (busyId) return;
    const note = window.prompt(
      `Private note for ${app.email} (kept internally — applicant won't see it):`,
      "",
    );
    if (note === null) return; // user cancelled
    setBusyId(app.application_id);
    try {
      await adminRejectAmbassadorApplication(app.application_id, { note });
      toast.success(`Marked rejected: ${app.email}`);
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't reject application.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const pending = (apps || []).filter((a) => a.status === "pending");
  const reviewed = (apps || []).filter((a) => a.status !== "pending");

  return (
    <Panel
      title="Applications"
      meta={`${pending.length} pending · ${reviewed.length} reviewed`}
      testid="admin-ambassador-applications"
    >
      <p className="text-sm text-white/55 mb-5 leading-relaxed">
        Public applications from <code className="bg-white/10 px-1 py-0.5 rounded text-[#D4B483]">/ambassadors/apply</code>. Approve mints an invite + emails the claim link via Resend. Reject is silent — soft rejections by silence are kinder.
      </p>

      {error && (
        <div className="text-sm text-[#F4B8C0] mb-4">
          Couldn't load applications: {String(error)}
        </div>
      )}
      {!error && apps === null && (
        <div className="py-6 text-sm text-white/55 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
      {!error && apps && apps.length === 0 && (
        <div className="py-8 text-center text-sm text-white/55">
          No applications yet. Share <code className="bg-white/10 px-1 py-0.5 rounded text-[#D4B483]">/ambassadors</code> on TikTok to start the funnel.
        </div>
      )}

      {!error && pending.length > 0 && (
        <div className="space-y-3 mb-6" data-testid="admin-apps-pending">
          {pending.map((app) => (
            <ApplicationCard
              key={app.application_id}
              app={app}
              busy={busyId === app.application_id}
              onApprove={() => approve(app)}
              onReject={() => reject(app)}
            />
          ))}
        </div>
      )}

      {!error && reviewed.length > 0 && (
        <details data-testid="admin-apps-reviewed">
          <summary className="text-xs uppercase tracking-[0.18em] text-white/45 cursor-pointer hover:text-white/65">
            Already reviewed ({reviewed.length})
          </summary>
          <div className="mt-3 space-y-2">
            {reviewed.map((app) => (
              <div
                key={app.application_id}
                className="bg-white/[0.02] border border-white/8 rounded-lg p-3 text-xs flex items-center justify-between gap-3 flex-wrap"
                data-testid={`admin-app-reviewed-${app.application_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-white/85 truncate">
                    {app.name} · {app.email}
                  </div>
                  <div className="text-white/40">
                    {app.status === "approved" ? "Approved" : "Rejected"}
                    {app.reviewed_at && ` · ${app.reviewed_at.slice(0, 10)}`}
                  </div>
                </div>
                <span
                  className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    app.status === "approved"
                      ? "bg-[#7BAE8E]/15 text-[#7BAE8E] border border-[#7BAE8E]/30"
                      : "bg-white/[0.04] text-white/45 border border-white/10"
                  }`}
                >
                  {app.status}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </Panel>
  );
};

const ApplicationCard = ({ app, busy, onApprove, onReject }) => (
  <div
    className="bg-white/[0.04] border border-white/10 rounded-xl p-4"
    data-testid={`admin-app-row-${app.application_id}`}
  >
    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="text-white font-medium">{app.name}</div>
        <div className="text-xs text-white/55">
          {app.email}
          {app.handle && <> · suggested @{app.handle}</>}
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">
        {app.created_at?.slice(0, 10)}
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-white/65 mb-3">
      {app.social_url && (
        <div className="truncate">
          <span className="text-white/40">Social: </span>
          <a
            href={app.social_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#D4B483] hover:underline inline-flex items-center gap-1"
          >
            {app.social_url.replace(/^https?:\/\//, "")}
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}
      {app.audience_size && (
        <div>
          <span className="text-white/40">Audience: </span>
          <span>{app.audience_size}</span>
        </div>
      )}
    </div>
    {app.why && (
      <p className="text-sm text-white/75 italic bg-white/[0.02] border-l-2 border-[#D4B483]/40 px-3 py-2 mb-3 rounded">
        “{app.why}”
      </p>
    )}
    <div className="flex items-center gap-2 flex-wrap">
      <button
        onClick={onApprove}
        disabled={busy}
        className="inline-flex items-center gap-1.5 bg-[#D4B483] text-[#1A1720] hover:bg-[#E5C99A] disabled:opacity-50 disabled:cursor-not-allowed px-4 py-1.5 rounded-full transition-colors text-xs font-medium"
        data-testid={`admin-app-approve-${app.application_id}`}
      >
        {busy ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <CheckCircle2 className="h-3 w-3" />
        )}
        Approve
      </button>
      <button
        onClick={onReject}
        disabled={busy}
        className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white border border-white/10 hover:border-white/20 px-3 py-1.5 rounded-full transition-colors disabled:opacity-30"
        data-testid={`admin-app-reject-${app.application_id}`}
      >
        Reject
      </button>
    </div>
  </div>
);


// ─── pending invites (invited but not yet activated) ──────────────
// The state that used to be invisible: approved/invited creators who
// haven't finished the one-step claim ("Catrine isn't in my roster").
const daysUntil = (iso) => {
  try {
    return Math.ceil((new Date(iso) - Date.now()) / 86400000);
  } catch {
    return null;
  }
};

const InviteStatusChip = ({ inv }) => {
  if (inv.expired)
    return (
      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#C4787C]/15 text-[#E29A9E] border border-[#C4787C]/30">
        Link expired
      </span>
    );
  if (inv.account_exists)
    return (
      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#D4B483]/15 text-[#D4B483] border border-[#D4B483]/30">
        Signed up — not activated
      </span>
    );
  return (
    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/[0.04] text-white/50 border border-white/10">
      Invited — hasn't signed up
    </span>
  );
};

const PendingInvites = ({ refreshSignal }) => {
  const [invites, setInvites] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await adminListAmbassadorInvites();
      setInvites(data.invites || []);
    } catch {
      setInvites([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  const copyLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(inv.claim_url);
      toast.success(`Claim link for ${inv.email} copied`);
    } catch {
      toast.error("Couldn't copy — your browser blocked clipboard access.");
    }
  };

  const resend = async (inv) => {
    setBusyId(inv.invite_id);
    try {
      // Reuses the invite endpoint: for an unclaimed email it re-sends
      // the SAME link (or a refreshed one if it had expired).
      const res = await adminCreateAmbassadorInvite({
        email: inv.email,
        send_email: true,
      });
      toast.success(
        res.sent_email
          ? `Claim link re-emailed to ${inv.email}`
          : `Invite refreshed for ${inv.email} — email didn't send, copy the link instead`,
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't resend invite.");
    } finally {
      setBusyId(null);
    }
  };

  if (!invites || invites.length === 0) return null; // nothing pending → no noise

  return (
    <Panel
      title="Pending invites"
      meta={`${invites.length} not yet activated`}
      testid="admin-pending-invites"
    >
      <p className="text-xs text-white/45 mb-4">
        Invited but haven't finished the one-step claim — they won't appear
        in the roster until they activate. Resend re-emails the same link
        (or a fresh one if it expired).
      </p>
      <div className="space-y-2.5">
        {invites.map((inv) => {
          const d = daysUntil(inv.expires_at);
          return (
            <div
              key={inv.invite_id}
              className="bg-white/[0.04] border border-white/10 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap"
              data-testid={`admin-invite-row-${inv.invite_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-white font-medium truncate">
                  {inv.name || inv.email}
                </div>
                <div className="text-xs text-white/55 truncate">
                  {inv.email} · @{inv.handle_suggested} ·{" "}
                  {fmtPct(inv.commission_rate)}
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/35 mt-1">
                  Invited {fmtDate(inv.created_at)}
                  {!inv.expired && d != null && ` · link expires in ${d}d`}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <InviteStatusChip inv={inv} />
                <button
                  onClick={() => copyLink(inv)}
                  className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white border border-white/10 hover:border-white/20 px-3 py-1.5 rounded-full transition-colors"
                  data-testid={`admin-invite-copy-${inv.invite_id}`}
                >
                  <Copy className="h-3 w-3" /> Copy link
                </button>
                <button
                  onClick={() => resend(inv)}
                  disabled={busyId === inv.invite_id}
                  className="inline-flex items-center gap-1.5 bg-[#D4B483] text-[#1A1720] hover:bg-[#E5C99A] disabled:opacity-50 px-4 py-1.5 rounded-full transition-colors text-xs font-medium"
                  data-testid={`admin-invite-resend-${inv.invite_id}`}
                >
                  {busyId === inv.invite_id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Send className="h-3 w-3" />
                  )}
                  Resend email
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

// ─── main page ────────────────────────────────────────────────────
const AdminAmbassadors = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [payoutTarget, setPayoutTarget] = useState(null);
  const [tab, setTab] = useState("roster"); // "roster" | "applications"
  // Bumped whenever an invite is created/approved so PendingInvites refetches.
  const [inviteBump, setInviteBump] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await adminListAmbassadors();
      setRows(data.ambassadors || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) setError("admin-only");
      else setError(err?.response?.data?.detail || err.message || "load failed");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Aggregate totals across all ambassadors.
  const totals = (rows || []).reduce(
    (acc, r) => ({
      clicks: acc.clicks + (r.clicks_total || 0),
      signups: acc.signups + (r.signups_total || 0),
      paying: acc.paying + (r.paying_users_total || 0),
      total: acc.total + (r.earnings_total_usd || 0),
      pending: acc.pending + (r.earnings_pending_usd || 0),
      paid: acc.paid + (r.earnings_paid_usd || 0),
    }),
    { clicks: 0, signups: 0, paying: 0, total: 0, pending: 0, paid: 0 },
  );

  const refreshAction = (
    <button
      onClick={load}
      className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/20 px-3 py-1.5 rounded-full transition-colors"
      data-testid="admin-ambassadors-refresh"
    >
      <RefreshCw className="h-3.5 w-3.5" />
      Refresh
    </button>
  );

  return (
    <AdminShell
      eyebrow="Ambassador program"
      title="Ambassadors"
      subtitle="Invite affiliates who earn a cut of every subscription they refer. Manage commission rates, view per-ambassador funnel performance, and mark payouts paid after you Venmo / PayPal them offline."
      actions={refreshAction}
    >
      <div data-testid="admin-ambassadors-page">
        {/* Tab toggle */}
        <div
          className="flex items-center gap-1 mb-5 bg-white/[0.03] border border-white/8 rounded-full p-1 w-fit"
          data-testid="admin-ambassadors-tabs"
        >
          <button
            onClick={() => setTab("roster")}
            className={`text-xs uppercase tracking-[0.18em] px-4 py-1.5 rounded-full transition-colors ${
              tab === "roster"
                ? "bg-[#D4B483] text-[#1A1720] font-medium"
                : "text-white/55 hover:text-white"
            }`}
            data-testid="admin-ambassadors-tab-roster"
          >
            Roster
          </button>
          <button
            onClick={() => setTab("applications")}
            className={`text-xs uppercase tracking-[0.18em] px-4 py-1.5 rounded-full transition-colors inline-flex items-center gap-1.5 ${
              tab === "applications"
                ? "bg-[#D4B483] text-[#1A1720] font-medium"
                : "text-white/55 hover:text-white"
            }`}
            data-testid="admin-ambassadors-tab-applications"
          >
            <Inbox className="h-3 w-3" /> Applications
          </button>
        </div>

        {tab === "applications" ? (
          <ApplicationsPanel onApproved={load} />
        ) : (
          <>
            {/* Top stats — aggregate program health */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <Stat
                icon={Users}
                label="Clicks"
                value={totals.clicks}
                testid="admin-ambassadors-stat-clicks"
              />
              <Stat
                icon={Plus}
                label="Signups"
                value={totals.signups}
                testid="admin-ambassadors-stat-signups"
              />
              <Stat
                icon={TrendingUp}
                label="Paying"
                value={totals.paying}
                testid="admin-ambassadors-stat-paying"
              />
              <Stat
                icon={DollarSign}
                label="Owed (pending)"
                value={fmtUsd(totals.pending)}
                testid="admin-ambassadors-stat-pending"
                highlight
              />
            </section>

            <InviteForm
              onSent={() => {
                load();
                setInviteBump((n) => n + 1);
              }}
            />

            <PendingInvites refreshSignal={inviteBump} />

        {/* Roster */}
        <Panel testid="admin-ambassadors-roster">
          <header className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-base font-medium text-white">
              All ambassadors ({rows?.length ?? 0})
            </h2>
            {totals.paid > 0 && (
              <span className="text-xs text-white/55">
                Lifetime paid out:{" "}
                <strong className="text-white">{fmtUsd(totals.paid)}</strong>
              </span>
            )}
          </header>
          {error === "admin-only" && (
            <div className="text-sm text-[#F4B8C0]">
              Admin access required to view this roster.
            </div>
          )}
          {error && error !== "admin-only" && (
            <div className="text-sm text-[#F4B8C0]">
              Couldn't load ambassadors: {String(error)}
            </div>
          )}
          {!error && rows === null && (
            <div className="py-6 text-sm text-white/55 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!error && rows && rows.length === 0 && (
            <div className="py-8 text-center text-sm text-white/55">
              No ambassadors yet. Send an invite above to get rolling.
            </div>
          )}
          {!error && rows && rows.length > 0 && (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.18em] text-white/45">
                  <tr>
                    <th className="text-left px-3 py-2">Ambassador</th>
                    <th className="text-right px-2 py-2">Rate</th>
                    <th className="text-right px-2 py-2">Clicks</th>
                    <th className="text-right px-2 py-2">Signups</th>
                    <th className="text-right px-2 py-2">Paying</th>
                    <th className="text-right px-2 py-2">Earned</th>
                    <th className="text-right px-2 py-2">Pending</th>
                    <th className="text-right px-3 py-2">Joined</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.handle}
                      className="border-t border-white/5 hover:bg-white/[0.03] text-white/90"
                      data-testid={`admin-ambassador-row-${r.handle}`}
                    >
                      <td className="px-3 py-3">
                        <div className="font-medium text-white">
                          {r.display_name || r.handle}
                          {r.needs_clawback_count > 0 && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#F4B8C0] bg-[#F4B8C0]/10 border border-[#F4B8C0]/30 px-1.5 py-0.5 rounded-full"
                              data-testid={`admin-ambassador-clawback-${r.handle}`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              clawback
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/45">
                          @{r.handle} · {r.email}
                        </div>
                      </td>
                      <td className="text-right px-2 py-3 text-white/75">
                        {fmtPct(r.commission_rate)}
                      </td>
                      <td className="text-right px-2 py-3 text-white/75 tabular-nums">
                        {r.clicks_total}
                      </td>
                      <td className="text-right px-2 py-3 text-white/75 tabular-nums">
                        {r.signups_total}
                      </td>
                      <td className="text-right px-2 py-3 text-white/75 tabular-nums">
                        {r.paying_users_total}
                      </td>
                      <td className="text-right px-2 py-3 text-white font-medium tabular-nums">
                        {fmtUsd(r.earnings_total_usd)}
                      </td>
                      <td className="text-right px-2 py-3 text-[#D4B483] font-medium tabular-nums">
                        {fmtUsd(r.earnings_pending_usd)}
                      </td>
                      <td className="text-right px-3 py-3 text-xs text-white/50 tabular-nums">
                        {fmtDate(r.joined_at)}
                      </td>
                      <td className="text-right px-3 py-3">
                        <button
                          onClick={() => setPayoutTarget(r)}
                          disabled={r.earnings_pending_usd <= 0}
                          className="text-xs inline-flex items-center gap-1 text-[#D4B483] hover:text-[#E5C99A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          data-testid={`admin-ambassador-pay-${r.handle}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Mark paid
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
          </>
        )}
      </div>

      <PayoutModal
        ambassador={payoutTarget}
        onClose={() => setPayoutTarget(null)}
        onPaid={load}
      />
    </AdminShell>
  );
};

const Stat = ({ icon: Icon, label, value, testid, highlight }) => (
  <div
    className={`bg-white/[0.03] border ${highlight ? "border-[#D4B483]/40" : "border-white/8"} rounded-2xl p-4`}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div
      className={`text-2xl font-light tabular-nums ${highlight ? "text-[#D4B483]" : "text-white"}`}
    >
      {value}
    </div>
  </div>
);

export default AdminAmbassadors;
